import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import styled from '@emotion/styled'
import * as API from '../../api'
import ReportFilterModal from './ReportFilterModal'
import {Bookmark, Plus, Trash2, Save, X} from 'lucide-react'

const FilterContainer = styled.div`
  .saved-filters {
    display: flex;
    align-items: center;
    gap: 16px;
    background: var(--kt-modal-bg, #fff);
    border-radius: 12px;
    padding: 14px 16px;
    border: 1px solid var(--kt-gray-200, #eff2f5);
  }
  .saved-filter-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .saved-filter-icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: var(--kt-gray-100, #f9f9f9);
    color: var(--kt-gray-600, #7e8299);
  }
  .saved-filter-heading strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: var(--kt-gray-800, #3f4254);
  }
  .saved-filter-heading p {
    margin: 3px 0 0;
    color: var(--kt-gray-600, #7e8299);
    font-size: 12px;
  }
  .saved-filter-controls {
    display: flex;
    flex: 1;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }
  .saved-select {
    flex: 1;
    min-width: 180px;
    position: relative;
  }
  .saved-select select {
    min-height: 40px;
    font-size: 13px;
    border-radius: 8px;
    padding-right: 68px;
  }
  .saved-select .saved-clear {
    position: absolute;
    right: 32px;
    top: 50%;
    transform: translateY(-50%);
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: var(--kt-gray-500, #a1a5b7);
    display: grid;
    place-items: center;
    padding: 0;
  }
  .saved-clear:hover,
  .saved-clear:focus-visible {
    color: var(--kt-gray-800, #3f4254);
    background: var(--kt-gray-100, #f9f9f9);
  }
  .saved-actions {
    display: flex;
    gap: 6px;
    align-items: center;
    margin-left: auto;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }
  .btn-icon {
    padding: 9px;
    min-width: 36px;
  }
  @media (max-width: 700px) {
    .saved-filters {
      flex-wrap: wrap;
      gap: 12px;
    }
    .saved-filter-controls {
      flex-basis: 100%;
    }
    .saved-filter-icon {
      display: none;
    }
  }
`

interface Props {
  name: string
  filter: any
  setFilter: any
}

const ReportFilter: FC<Props> = (props: Props) => {
  const [filters, setFilters] = useState<any[]>([])
  const [selectedFilter, setSelectedFilter] = useState<any>('none')
  const [currentFilter, setCurrentFilter] = useState<any>(null)
  const [filterModal, showFilterModal] = useState<boolean>(false)
  const [updateLoading, setUpdateLoading] = useState<boolean>(false)
  const [removeLoading, setRemoveLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState(false)

  const getFilters = async () => {
    if (!props.name) return
    setLoadError(false)
    try {
      const result = await API.getReportFilters(props.name)
      if (!result?.success || !Array.isArray(result.data)) throw new Error('FILTERS_FAILED')
      setFilters(
        result.data.map((item: any) => ({
          ...item,
          data: typeof item.data === 'string' ? JSON.parse(item.data) : item.data,
        }))
      )
    } catch {
      setLoadError(true)
    }
  }

  useEffect(() => {
    getFilters()
  }, [props.name])

  useEffect(() => {
    if (!props.filter) return
    setCurrentFilter(props.filter)
  }, [props.filter])

  const updateFilter = async () => {
    try {
      setUpdateLoading(true)
      const currentFilter = getFilterById(selectedFilter)
      await API.updateReportFilter({
        id_report_filter: currentFilter.id_report_filter,
        filters: props.filter,
      })
      await getFilters()
    } catch (e) {
      console.error(e)
    } finally {
      setUpdateLoading(false)
    }
  }

  const removeFilter = async () => {
    try {
      setRemoveLoading(true)
      const currentFilter = getFilterById(selectedFilter)
      await API.removeReportFilter({
        id_report_filter: currentFilter.id_report_filter,
      })
      await getFilters()
      setSelectedFilter('none')
    } catch (e) {
      console.error(e)
    } finally {
      setRemoveLoading(false)
    }
  }

  const getFilterById = (id: any) => {
    return filters.find((i) => i.id_report_filter == id)
  }

  return (
    <FilterContainer>
      <Modal show={filterModal} onHide={() => showFilterModal(false)} centered size='lg'>
        <ReportFilterModal
          report={props.name}
          filters={currentFilter}
          onClose={(success: boolean) => {
            showFilterModal(false)
            if (success) getFilters()
          }}
        />
      </Modal>

      <div className='saved-filters'>
        <div className='saved-filter-heading'>
          <span className='saved-filter-icon'>
            <Bookmark size={17} aria-hidden='true' />
          </span>
          <div>
            <strong>Filtros salvos</strong>
            {!filters.length && <p>Guarde suas seleções para usar depois.</p>}
          </div>
        </div>
        {filters.length > 0 ? (
          <div className='saved-filter-controls'>
            <div className='saved-select'>
              <select
                className='form-select'
                aria-label='Filtro salvo'
                value={selectedFilter}
                onChange={({target: {value}}) => {
                  setSelectedFilter(value)
                  props.setFilter(getFilterById(value))
                }}
              >
                <option value='none'>Selecione um filtro salvo</option>
                {filters.map((filter, index) => (
                  <option key={filter.id_report_filter} value={filter.id_report_filter}>
                    {filter.name}
                  </option>
                ))}
              </select>
              {selectedFilter !== 'none' && (
                <button
                  type='button'
                  className='saved-clear'
                  aria-label='Limpar filtro salvo'
                  title='Limpar filtro salvo'
                  onClick={() => {
                    setSelectedFilter('none')
                    props.setFilter(null)
                  }}
                >
                  <X size={15} aria-hidden='true' />
                </button>
              )}
            </div>

            <div className='saved-actions'>
              {selectedFilter !== 'none' && (
                <>
                  <button
                    type='button'
                    className='btn btn-icon btn-light-primary'
                    aria-label='Atualizar filtro salvo'
                    title='Atualizar filtro salvo'
                    onClick={updateFilter}
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                    ) : (
                      <Save size={16} aria-hidden='true' />
                    )}
                  </button>

                  <button
                    type='button'
                    className='btn btn-icon btn-light-danger'
                    aria-label='Remover filtro salvo'
                    title='Remover filtro salvo'
                    onClick={removeFilter}
                    disabled={removeLoading}
                  >
                    {removeLoading ? (
                      <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                    ) : (
                      <Trash2 size={16} aria-hidden='true' />
                    )}
                  </button>
                </>
              )}

              <button
                type='button'
                className='btn btn-light-primary'
                onClick={() => showFilterModal(true)}
              >
                <Plus size={16} aria-hidden='true' />
                Novo Filtro
              </button>
            </div>
          </div>
        ) : (
          <div className='saved-actions'>
            <button
              type='button'
              className='btn btn-light-primary'
              onClick={() => showFilterModal(true)}
            >
              <Plus size={16} aria-hidden='true' />
              Salvar filtro atual
            </button>
          </div>
        )}
      </div>
      {loadError && (
        <div className='alert alert-warning mt-3 mb-0 py-3' role='alert'>
          Não foi possível carregar os filtros salvos.{' '}
          <button type='button' className='btn btn-link p-0' onClick={getFilters}>
            Tentar novamente
          </button>
        </div>
      )}
    </FilterContainer>
  )
}

export default ReportFilter
